import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { areEqual } from 'Utils/equalityChecks';
import { Api } from 'Utils/api';

import { ReferralPartnerModal } from '../ReferralPartnerModal';
import { Option, PipelineOption, CustomFieldConfig, ExistingLeadFormData, StageOption, FormFieldSetting } from '../types';

import classes from './existingLeadForm.module.css';

interface ExistingLeadFormProps {
  locationId: string;
  ssoToken: string;
  existingLeadOptions: Option[];
  referralPartnerOptions: Option[];
  pipelineOptions: PipelineOption[];
  companyOptions: Option[];
  customFieldConfig: CustomFieldConfig;
  formFieldSettings: FormFieldSetting[];
  isLoadingContacts: boolean;
  isLoadingCompanies: boolean;
  isLoadingPipelines: boolean;
  pipelineStorageKey: string | null;
  onReferralPartnerCreated: (partner: Option) => void;
  onCompanyCreated: (company: Option) => void;
}

const defaultFormValues: ExistingLeadFormData = {
  locationId: '',
  existingLeadId: '',
  existingLeadName: '',
  referredBy: '',
  referredById: '',
  pipelineId: '',
  stageId: '',
  monetaryValue: '',
  opportunityName: '',
  leadValue: '',
  office: '',
  extraFields: {},
};

function ExistingLeadFormComponent({
  locationId,
  ssoToken,
  existingLeadOptions,
  referralPartnerOptions,
  pipelineOptions,
  companyOptions,
  customFieldConfig,
  formFieldSettings,
  isLoadingContacts,
  isLoadingCompanies,
  isLoadingPipelines,
  pipelineStorageKey,
  onReferralPartnerCreated,
  onCompanyCreated,
}: ExistingLeadFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showReferralPartnerModal, setShowReferralPartnerModal] = useState(false);
  const [selectedLeadLabel, setSelectedLeadLabel] = useState('');
  const [existingLeadSearchTerm, setExistingLeadSearchTerm] = useState('');
  const [isExistingLeadInputFocused, setIsExistingLeadInputFocused] = useState(false);
  const [selectedReferralPartnerLabel, setSelectedReferralPartnerLabel] = useState('');
  const [referralPartnerSearchTerm, setReferralPartnerSearchTerm] = useState('');
  const [isReferralInputFocused, setIsReferralInputFocused] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm<ExistingLeadFormData>({
    mode: 'onChange',
    defaultValues: defaultFormValues,
  });

  const hasPipelines = pipelineOptions.length > 0;
  const selectedPipelineId = watch('pipelineId');

  // Get stages for the selected pipeline
  const stageOptions: StageOption[] = useMemo(() => {
    if (!selectedPipelineId) return [];
    const selectedPipeline = pipelineOptions.find((p) => p.id === selectedPipelineId);
    return selectedPipeline?.stages || [];
  }, [pipelineOptions, selectedPipelineId]);

  useEffect(() => {
    if (pipelineStorageKey && hasPipelines) {
      const storedPipelineId = localStorage.getItem(pipelineStorageKey);
      if (storedPipelineId && pipelineOptions.some((p) => p.id === storedPipelineId)) {
        setValue('pipelineId', storedPipelineId, { shouldValidate: true });
        // Auto-select first stage
        const pipeline = pipelineOptions.find((p) => p.id === storedPipelineId);
        if (pipeline?.stages?.length) {
          setValue('stageId', pipeline.stages[0].id, { shouldValidate: true });
        }
      }
    }
  }, [pipelineStorageKey, hasPipelines, pipelineOptions, setValue]);

  // Filter existing leads based on search (show all if no search term)
  const filteredExistingLeads = useMemo(() => {
    if (!existingLeadSearchTerm) return existingLeadOptions;
    const term = existingLeadSearchTerm.toLowerCase();
    return existingLeadOptions.filter((option) => option.label.toLowerCase().includes(term));
  }, [existingLeadOptions, existingLeadSearchTerm]);

  // Filter referral partners based on search (show all if no search term)
  const filteredReferralPartners = useMemo(() => {
    if (!referralPartnerSearchTerm) return referralPartnerOptions;
    const term = referralPartnerSearchTerm.toLowerCase();
    return referralPartnerOptions.filter((option) =>
      option.label.toLowerCase().includes(term)
      || (option.companyName && option.companyName.toLowerCase().includes(term))
    );
  }, [referralPartnerOptions, referralPartnerSearchTerm]);

  const handleExistingLeadSelect = useCallback((option: Option) => {
    setValue('existingLeadId', option.id, { shouldValidate: true, shouldDirty: true });
    setValue('existingLeadName', option.label);
    setSelectedLeadLabel(option.label);
    setExistingLeadSearchTerm('');
  }, [setValue]);

  const handleClearExistingLead = useCallback(() => {
    setValue('existingLeadId', '', { shouldValidate: true, shouldDirty: true });
    setValue('existingLeadName', '');
    setSelectedLeadLabel('');
    setExistingLeadSearchTerm('');
  }, [setValue]);

  const handleReferralPartnerSelect = useCallback((option: Option) => {
    setValue('referredById', option.id);
    setValue('referredBy', option.label);
    setSelectedReferralPartnerLabel(option.companyName ? `${option.label} - ${option.companyName}` : option.label);
    setReferralPartnerSearchTerm('');
  }, [setValue]);

  const handleClearReferralPartner = useCallback(() => {
    setValue('referredById', '');
    setValue('referredBy', '');
    setSelectedReferralPartnerLabel('');
    setReferralPartnerSearchTerm('');
  }, [setValue]);

  const handleReferralPartnerCreated = useCallback((partner: Option) => {
    onReferralPartnerCreated(partner);
    setValue('referredById', partner.id);
    setValue('referredBy', partner.label);
    setSelectedReferralPartnerLabel(partner.companyName ? `${partner.label} - ${partner.companyName}` : partner.label);
    setShowReferralPartnerModal(false);
  }, [onReferralPartnerCreated, setValue]);

  const handlePipelineChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setValue('pipelineId', value, { shouldValidate: true });
    if (pipelineStorageKey) {
      if (value) {
        localStorage.setItem(pipelineStorageKey, value);
      } else {
        localStorage.removeItem(pipelineStorageKey);
      }
    }
    // Auto-select first stage when pipeline changes
    if (value) {
      const selectedPipeline = pipelineOptions.find((p) => p.id === value);
      if (selectedPipeline?.stages?.length) {
        setValue('stageId', selectedPipeline.stages[0].id, { shouldValidate: true });
      } else {
        setValue('stageId', '', { shouldValidate: true });
      }
    } else {
      setValue('stageId', '', { shouldValidate: true });
    }
  }, [setValue, pipelineStorageKey, pipelineOptions]);

  const onSubmit = handleSubmit(async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      if (!data.existingLeadId) {
        setErrorMessage('Please select an existing lead.');
        return;
      }

      const opportunityCustomFields: { id: string; field_value: string }[] = [];

      if (data.referredById && customFieldConfig.oppReferredById) {
        opportunityCustomFields.push({ id: customFieldConfig.oppReferredById, field_value: data.referredById });
      }
      if (data.referredBy && customFieldConfig.oppReferredBy) {
        opportunityCustomFields.push({ id: customFieldConfig.oppReferredBy, field_value: data.referredBy });
      }
      if (data.leadValue && customFieldConfig.leadValueId) {
        opportunityCustomFields.push({ id: customFieldConfig.leadValueId, field_value: data.leadValue });
      }
      if (data.office && customFieldConfig.officeId) {
        opportunityCustomFields.push({ id: customFieldConfig.officeId, field_value: data.office });
      }

      // Dynamic custom fields from settings (split by prefix)
      const contactExtraFields: { id: string; field_value: string }[] = [];

      Object.entries(data.extraFields || {}).forEach(([fieldId, value]) => {
        if (value) {
          const setting = formFieldSettings.find((s) => s.fieldId === fieldId);
          if (setting) {
            const fieldEntry = { id: setting.fieldId, field_value: value as string };
            if (setting.fieldKey.startsWith('contact.')) {
              contactExtraFields.push(fieldEntry);
            } else if (setting.fieldKey.startsWith('opportunity.')) {
              opportunityCustomFields.push(fieldEntry);
            }
          }
        }
      });

      const contactCustomFields: { id: string; field_value: string }[] = [];

      if (data.referredById && customFieldConfig.contactReferredById) {
        contactCustomFields.push({ id: customFieldConfig.contactReferredById, field_value: data.referredById });
      }
      if (data.referredBy && customFieldConfig.contactReferredBy) {
        contactCustomFields.push({ id: customFieldConfig.contactReferredBy, field_value: data.referredBy });
      }
      contactCustomFields.push(...contactExtraFields);

      // Backend expects snake_case field names
      if (contactCustomFields.length > 0) {
        try {
          await Api.put(
            `/ghl/embed/contacts/${data.existingLeadId}`,
            { custom_fields: contactCustomFields },
            { headers: { 'X-GHL-SSO': ssoToken } }
          );
        } catch (updateError) {
          // eslint-disable-next-line no-console
          console.error('Failed to update lead custom fields', updateError);
        }
      }

      const defaultName = data.existingLeadName || 'Referral Opportunity';
      const opportunityName = data.opportunityName?.trim() || defaultName;

      const opportunityPayload: any = {
        pipeline_id: data.pipelineId,
        pipeline_stage_id: data.stageId,
        name: opportunityName,
        status: 'open',
        contact_id: data.existingLeadId,
        custom_fields: opportunityCustomFields,
      };

      if (data.referredBy) {
        opportunityPayload.source = data.referredBy;
      }

      if (data.monetaryValue) {
        opportunityPayload.monetary_value = parseFloat(data.monetaryValue);
      }

      if (data.referredById) {
        opportunityPayload.referral_partner_id = data.referredById;
      }

      if (data.leadValue) {
        opportunityPayload.lead_value = data.leadValue;
      }

      if (data.office) {
        opportunityPayload.office = data.office;
      }

      await Api.post('/ghl/embed/opportunities', opportunityPayload, {
        headers: { 'X-GHL-SSO': ssoToken },
      });

      setSuccessMessage('Opportunity created successfully!');

      setTimeout(() => {
        setSuccessMessage(null);
        reset(defaultFormValues);
        setSelectedLeadLabel('');
        setSelectedReferralPartnerLabel('');
        if (pipelineStorageKey && hasPipelines) {
          const storedPipelineId = localStorage.getItem(pipelineStorageKey);
          const pipeline = pipelineOptions.find((p) => p.id === storedPipelineId);
          if (storedPipelineId && pipeline) {
            setValue('pipelineId', storedPipelineId, { shouldValidate: true });
            if (pipeline.stages?.length) {
              setValue('stageId', pipeline.stages[0].id, { shouldValidate: true });
            }
          }
        }
      }, 3000);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to create opportunity', error);
      const message = error?.response?.data?.message || error?.message || 'Failed to create opportunity';
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <div className={classes.formContainer}>
      {errorMessage && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {errorMessage}
          <button type="button" className="btn-close" onClick={() => setErrorMessage(null)} aria-label="Close" />
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)} aria-label="Close" />
        </div>
      )}

      <form onSubmit={onSubmit}>
        <div className="mb-3">
          <label htmlFor="existingLeadId" className="form-label">
            Existing Lead <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <div className={`form-control ${classes.autocompleteWrapper}`}>
              {selectedLeadLabel ? (
                <div className={classes.selectedValue}>
                  <span>{selectedLeadLabel}</span>
                  <button type="button" className="btn-close btn-sm" onClick={handleClearExistingLead} aria-label="Clear" />
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    className={classes.autocompleteInput}
                    placeholder="Search for leads..."
                    value={existingLeadSearchTerm}
                    onChange={(e) => setExistingLeadSearchTerm(e.target.value)}
                    onFocus={() => setIsExistingLeadInputFocused(true)}
                    onBlur={() => setTimeout(() => setIsExistingLeadInputFocused(false), 200)}
                  />
                  {isExistingLeadInputFocused && filteredExistingLeads.length > 0 && (
                    <ul className={classes.autocompleteDropdown}>
                      {filteredExistingLeads.slice(0, 10).map((option) => (
                        <li key={option.id}>
                          <button type="button" onClick={() => handleExistingLeadSelect(option)}>
                            {option.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {isExistingLeadInputFocused && existingLeadSearchTerm && filteredExistingLeads.length === 0 && (
                    <ul className={classes.autocompleteDropdown}>
                      <li>
                        <span className={classes.emptyOption}>No matches found</span>
                      </li>
                    </ul>
                  )}
                  {isLoadingContacts && <div className={classes.loadingIndicator}>Loading...</div>}
                </>
              )}
            </div>
          </div>
          <input type="hidden" {...register('existingLeadId', { required: 'Existing lead is required' })} />
          {errors.existingLeadId && <div className="text-danger small">{errors.existingLeadId.message}</div>}
        </div>

        {/* Referred By - Autocomplete with add button */}
        <div className="mb-3">
          <label htmlFor="referredBy" className="form-label">Referred By</label>
          <div className="input-group">
            <div className={`form-control ${classes.autocompleteWrapper}`}>
              {selectedReferralPartnerLabel ? (
                <div className={classes.selectedValue}>
                  <span>{selectedReferralPartnerLabel}</span>
                  <button type="button" className="btn-close btn-sm" onClick={handleClearReferralPartner} aria-label="Clear" />
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    className={classes.autocompleteInput}
                    placeholder="Search for contacts..."
                    value={referralPartnerSearchTerm}
                    onChange={(e) => setReferralPartnerSearchTerm(e.target.value)}
                    onFocus={() => setIsReferralInputFocused(true)}
                    onBlur={() => setTimeout(() => setIsReferralInputFocused(false), 200)}
                  />
                  {isReferralInputFocused && filteredReferralPartners.length > 0 && (
                    <ul className={classes.autocompleteDropdown}>
                      {filteredReferralPartners.slice(0, 10).map((option) => (
                        <li key={option.id}>
                          <button type="button" onClick={() => handleReferralPartnerSelect(option)}>
                            {option.label}
                            {option.companyName && (
                              <span className={classes.companyName}> - {option.companyName}</span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {isReferralInputFocused && referralPartnerSearchTerm && filteredReferralPartners.length === 0 && (
                    <ul className={classes.autocompleteDropdown}>
                      <li>
                        <span className={classes.emptyOption}>No matches found</span>
                      </li>
                    </ul>
                  )}
                  {isLoadingContacts && <div className={classes.loadingIndicator}>Loading...</div>}
                </>
              )}
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setShowReferralPartnerModal(true)}
              title="Add new referral partner"
            >
              +
            </button>
          </div>
        </div>

        {/* Pipeline */}
        <div className="mb-3">
          <label htmlFor="pipelineId" className="form-label">
            Pipeline {hasPipelines && <span className="text-danger">*</span>}
          </label>
          <Controller
            name="pipelineId"
            control={control}
            rules={{ required: hasPipelines ? 'Pipeline is required' : false }}
            render={({ field }) => (
              <select
                className={`form-select ${errors.pipelineId ? 'is-invalid' : ''}`}
                id="pipelineId"
                disabled={!hasPipelines || isLoadingPipelines}
                value={field.value}
                onChange={handlePipelineChange}
              >
                <option value="">Select a pipeline...</option>
                {pipelineOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          />
          {errors.pipelineId && <div className="invalid-feedback">{errors.pipelineId.message}</div>}
        </div>

        {/* Pipeline Stage */}
        {stageOptions.length > 0 && (
          <div className="mb-3">
            <label htmlFor="stageId" className="form-label">
              Pipeline Stage <span className="text-danger">*</span>
            </label>
            <Controller
              name="stageId"
              control={control}
              rules={{ required: stageOptions.length > 0 ? 'Pipeline stage is required' : false }}
              render={({ field }) => (
                <select
                  className={`form-select ${errors.stageId ? 'is-invalid' : ''}`}
                  id="stageId"
                  value={field.value}
                  onChange={(e) => setValue('stageId', e.target.value, { shouldValidate: true })}
                >
                  {stageOptions.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.label}
                    </option>
                  ))}
                </select>
              )}
            />
            {errors.stageId && <div className="invalid-feedback">{errors.stageId.message}</div>}
          </div>
        )}

        {/* Opportunity Name */}
        <div className="mb-3">
          <label htmlFor="opportunityName" className="form-label">Opportunity Name</label>
          <input
            type="text"
            className="form-control"
            id="opportunityName"
            placeholder="Defaults to lead name if left blank"
            {...register('opportunityName')}
          />
        </div>

        {/* Opportunity Value */}
        <div className="mb-3">
          <label htmlFor="monetaryValue" className="form-label">Opportunity Value</label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              type="text"
              inputMode="decimal"
              className="form-control"
              id="monetaryValue"
              placeholder="0.00"
              {...register('monetaryValue', {
                pattern: {
                  value: /^\d*\.?\d{0,2}$/,
                  message: 'Enter a valid dollar amount',
                },
              })}
            />
          </div>
          {errors.monetaryValue && <div className="text-danger small">{errors.monetaryValue.message}</div>}
        </div>

        {/* Lead Value */}
        {customFieldConfig.leadValueId && (
          <div className="mb-3">
            <label htmlFor="leadValue" className="form-label">Lead Value</label>
            {customFieldConfig.leadValueOptions.length > 0 ? (
              <select className="form-select" id="leadValue" {...register('leadValue')}>
                {customFieldConfig.leadValueOptions.map((option) => (
                  <option key={option} value={option}>
                    {option || 'Select...'}
                  </option>
                ))}
              </select>
            ) : (
              <input type="text" className="form-control" id="leadValue" {...register('leadValue')} />
            )}
          </div>
        )}

        {/* Office */}
        {customFieldConfig.officeOptions.length > 0 && (
          <div className="mb-3">
            <label htmlFor="office" className="form-label">Office</label>
            <select className="form-select" id="office" {...register('office')}>
              {customFieldConfig.officeOptions.map((option) => (
                <option key={option} value={option}>
                  {option || 'Select...'}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Dynamic Custom Fields */}
        {formFieldSettings
          .filter((s) => s.applyTo === 'existing' || s.applyTo === 'both')
          .map((setting) => (
            <div key={setting.fieldId} className="mb-3">
              <label htmlFor={setting.fieldId} className="form-label">{setting.label}</label>
              {setting.picklistOptions.length > 0 ? (
                <select className="form-select" id={setting.fieldId} {...register(`extraFields.${setting.fieldId}`)}>
                  <option value="">Select...</option>
                  {setting.picklistOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : setting.dataType === 'LARGE_TEXT' ? (
                <textarea
                  className="form-control"
                  id={setting.fieldId}
                  rows={3}
                  {...register(`extraFields.${setting.fieldId}`)}
                />
              ) : (
                <input
                  type="text"
                  className="form-control"
                  id={setting.fieldId}
                  {...register(`extraFields.${setting.fieldId}`)}
                />
              )}
            </div>
          ))}

        <button
          type="submit"
          className="btn btn-primary w-100"
          disabled={!hasPipelines || !isDirty || !isValid || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Creating...
            </>
          ) : (
            'Create Opportunity'
          )}
        </button>
      </form>

      <ReferralPartnerModal
        isOpen={showReferralPartnerModal}
        onClose={() => setShowReferralPartnerModal(false)}
        locationId={locationId}
        ssoToken={ssoToken}
        companyOptions={companyOptions}
        customFieldConfig={customFieldConfig}
        isLoadingCompanies={isLoadingCompanies}
        onReferralPartnerCreated={handleReferralPartnerCreated}
        onCompanyCreated={onCompanyCreated}
      />
    </div>
  );
}

const ExistingLeadFormMemo = memo(ExistingLeadFormComponent, areEqual);

export { ExistingLeadFormMemo as ExistingLeadForm };
